import { FeasibilityQuotaGroup } from '../../models/feasibility/feasibility.model';
import { ChallengingQuota } from '../../models/feasibility/challenging-quota.model';
import { Quota, QuotaCriteria } from '../../models/survey-quota.model';
import { quotaCriterionLabels } from '../quota-title/quota-title.helper';

/**
 * `Gender, Age nested` — the qualification names behind a quota. The suffix
 * marks a nest, which is what makes a one-quota banner read correctly.
 */
function qualificationLabelOf(criteria: readonly QuotaCriteria[]): string {
  const names = criteria
    .map((criterion) => criterion.qualificationName)
    .join(', ');

  return criteria.length > 1 ? `${names} nested` : names;
}

/** The buyer's own title wins; a single criterion names itself; a nest falls back to its group. */
function titleOf(quota: Quota): string {
  if (quota.quotaTitle) {
    return quota.quotaTitle;
  }

  const [firstCriterion] = quota.criteria;

  return quota.criteria.length === 1 && firstCriterion
    ? firstCriterion.qualificationName
    : quota.groupKey;
}

function toChallengingQuota(quota: Quota): ChallengingQuota {
  return {
    buyerQuotaId: quota.buyerQuotaId,
    title: titleOf(quota),
    qualificationLabel: qualificationLabelOf(quota.criteria),
    conditions: quota.criteria.flatMap(quotaCriterionLabels),
    qualificationCodes: quota.criteria.map(
      (criterion) => criterion.qualificationCode,
    ),
    requiredCount: quota.requiredCount,
    percentage: quota.percentage,
    minimum: quota.minimum,
    maximum: quota.maximum,
  };
}

/**
 * The buyer's quotas that were called out as challenging. Every displayed field
 * comes from the quota itself — the caller contributes only the id set, so an
 * id with no matching quota is dropped rather than rendered half-empty.
 */
export function toChallengingQuotas(
  challengingIds: ReadonlySet<string>,
  quotas: readonly Quota[],
): ChallengingQuota[] {
  return quotas
    .filter((quota) => challengingIds.has(quota.buyerQuotaId))
    .map(toChallengingQuota);
}

/**
 * The difficult ids inside a feasibility estimate's quota groups. The verdict
 * rides on each row (see FeasibilityQuotaMapper) rather than as a flat list on
 * the slice, so the set is rebuilt from the rows on read.
 */
export function toDifficultQuotaIds(
  groups: readonly FeasibilityQuotaGroup[],
): ReadonlySet<string> {
  return new Set(
    groups.flatMap(({ quotas }) =>
      quotas.filter((row) => row.isDifficult).map((row) => row.buyerQuotaId),
    ),
  );
}

/** Set difference — the ids a stronger warning has already claimed. */
export function withoutIds(
  ids: ReadonlySet<string>,
  excluded: readonly string[],
): ReadonlySet<string> {
  const excludedIds = new Set(excluded);

  return new Set([...ids].filter((id) => !excludedIds.has(id)));
}
